// iter291 · Founding Athlete program UI kit.
//
// Exports three tightly-coupled surfaces that together deliver the
// "first 500 athletes" experience:
//
//   1. <FoundingAthletePill /> — the amber-glow chip that replaces the
//      Athlete · Sport · Team subhead on AthleteSharePage (variant
//      "profile") and renders the wide live-counter row above "Find
//      Athletes" on the network home (variant "banner").
//
//   2. <FoundingAthleteModal /> — the "glass case" sheet the pill opens.
//      Shows the live X / 500 counter, program perks, and CTAs to
//      contact the founder directly OR (for un-claimed logged-in
//      athletes) claim their spot.
//
//   3. <FoundingContactSheet /> — a lightweight composer that lands a
//      note in the Founder Inbox under the "Founding Athletes" tab.
//
// All three share the same design language — soft amber glow, glass
// blur, tight Apple-style spacing — so the experience feels like one
// consistent thread whether you tap the pill on a bio, the banner on
// the home feed, or the contact button on the athlete dashboard.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles, X, Star, MessageCircle, ArrowRight, ArrowUpRight, Send, Check, Loader2, ShieldCheck, ChevronDown } from 'lucide-react';
import { FlytWings } from '../FlytWings';
import axios from 'axios';
import { toast } from 'sonner';
import { useUserAuth } from '../../contexts/UserAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Shared amber gradient — used across the pill, banner and modal so
// the visual language stays consistent whichever surface the athlete
// hits first.
const AMBER_GRADIENT = 'linear-gradient(135deg, rgba(250,204,21,0.24), rgba(251,191,36,0.16) 45%, rgba(217,119,6,0.14))';
const AMBER_GLOW_SHADOW = '0 0 32px -12px rgba(250,204,21,0.55), inset 0 1px 0 rgba(255,255,255,0.12)';

// Lightweight in-memory cache so we don't refetch stats on every mount.
// 30 second TTL keeps the counter live enough for launch day without
// pounding the endpoint from every persona surface at once.
let _statsCache = { data: null, at: 0 };
const STATS_TTL_MS = 30_000;

async function fetchStats(force = false) {
  const now = Date.now();
  if (!force && _statsCache.data && now - _statsCache.at < STATS_TTL_MS) {
    return _statsCache.data;
  }
  const { data } = await axios.get(`${API}/founding-athletes/stats`);
  _statsCache = { data, at: now };
  return data;
}

export function useFoundingAthleteStats(auto = true) {
  const [stats, setStats] = useState(_statsCache.data);
  const [loading, setLoading] = useState(!_statsCache.data);
  const refresh = useCallback(async (force = false) => {
    setLoading(true);
    try {
      const data = await fetchStats(force);
      setStats(data);
    } catch (e) {
      // swallow — pill / banner fall back to hidden state
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { if (auto) refresh(false); }, [auto, refresh]);
  return { stats, loading, refresh };
}

// ─────────────────────────────────────────────────────────────────────
// PILL / BANNER
// ─────────────────────────────────────────────────────────────────────

export function FoundingAthletePill({
  variant = 'profile', // "profile" | "banner"
  number,              // athlete's founding number if already claimed
  onClick,
  className = '',
  showRemaining = true,
  compact = false,
}) {
  const { stats } = useFoundingAthleteStats(variant === 'banner');
  const remaining = stats?.remaining;

  if (variant === 'banner') {
    return <FoundingAthleteBanner onOpenModal={onClick} className={className} />;
  }

  // "profile" variant — replaces the subhead on the share page.
  const label = number ? `Founding Athlete · #${number}` : 'Founding Athlete';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full ${compact ? 'px-2.5 py-0.5' : 'px-3 py-1'} transition active:scale-95 ${className}`}
      style={{ background: AMBER_GRADIENT, boxShadow: AMBER_GLOW_SHADOW }}
      data-testid="founding-athlete-pill"
    >
      <Star className={`${compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} text-amber-200 fill-amber-300`} strokeWidth={2} />
      <span className={`font-display ${compact ? 'text-[9px]' : 'text-[10px]'} uppercase tracking-[0.28em] text-amber-100 font-bold whitespace-nowrap`}>
        {label}
      </span>
      {showRemaining && typeof remaining === 'number' && !number && (
        <span className="text-[9px] uppercase tracking-widest text-amber-200/70 pl-1 border-l border-amber-200/25 ml-1">
          {remaining} left
        </span>
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// BANNER — expandable scoreboard on the Network home
// ─────────────────────────────────────────────────────────────────────
// Two-row layout:
//   • Top row: FLYT amber pill with live count + (if athlete has
//     claimed) an "→ your profile" chip + a chevron that toggles the
//     leaderboard drawer.
//   • Bottom drawer (collapsed by default): "The First 250"
//     leaderboard — every claimed athlete by number with avatar,
//     name, and click-through to their FLYT ID™.

function FoundingAthleteBanner({ onOpenModal, className = '' }) {
  const { user } = useUserAuth();
  const { stats } = useFoundingAthleteStats(true);
  const [open, setOpen]  = useState(false);
  const [board, setBoard] = useState([]);
  const [boardLoading, setBoardLoading] = useState(false);
  const fetchedRef = useRef(false);
  // iter291 · Founding fields flow via `user.linked_athlete` (from the
  // `/me` endpoint). Fall back to top-level fields for future-proofing.
  const linked  = user?.linked_athlete || {};
  const myNumber = linked.founding_athlete_number || user?.founding_athlete_number || null;
  const mySlug   = linked.slug || user?.athlete_slug || null;

  const loadBoard = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setBoardLoading(true);
    try {
      const { data } = await axios.get(`${API}/founding-athletes/leaderboard`);
      setBoard(data.items || []);
    } catch {
      /* leaderboard is optional — swallow */
    } finally {
      setBoardLoading(false);
    }
  }, []);

  const toggle = useCallback(() => {
    setOpen((v) => {
      const next = !v;
      if (next) loadBoard();
      return next;
    });
  }, [loadBoard]);

  const remaining = stats?.remaining;
  const claimed   = stats?.claimed;
  const cap       = stats?.cap || 500;
  const iAmClaimed = !!myNumber;

  return (
    <div className={`w-full ${className}`} data-testid="founding-athlete-banner-wrap">
      {/* iter680 · Liquid Glass surface — matches the home nav
          (translucent white fill + subtle ring + backdrop blur)
          instead of the old solid amber gradient + heavy glow. Amber
          is kept only as an accent (icon, kicker, numbers) so it still
          reads as the Founding program. */}
      <div
        className="relative rounded-2xl overflow-hidden bg-white/[0.06] backdrop-blur-xl"
      >
        <span aria-hidden className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/15 pointer-events-none" />
        <span aria-hidden className="absolute -top-16 -right-10 w-40 h-40 rounded-full bg-amber-300/10 blur-3xl pointer-events-none" />

        {/* Header row — tap opens modal, chevron toggles leaderboard */}
        <div className="relative flex items-center gap-3 px-4 py-3">
          <button
            type="button"
            onClick={onOpenModal}
            className="flex-1 min-w-0 flex items-center gap-3 text-left active:scale-[0.99] transition"
            data-testid="founding-athlete-banner"
          >
            <div className="w-9 h-9 rounded-full bg-black/40 ring-1 ring-white/20 flex items-center justify-center flex-shrink-0 backdrop-blur">
              <FlytWings className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-display text-[10px] uppercase tracking-[0.28em] text-white/90 leading-none">
                Founding Athlete Program · Live
              </p>
              <p className="mt-1 text-sm sm:text-[15px] text-white leading-snug">
                {iAmClaimed ? (
                  <>You&apos;re <span className="font-bold text-white">#{myNumber}</span> · {claimed ?? '—'} / {cap} claimed</>
                ) : typeof remaining === 'number' ? (
                  <>
                    <span className="font-bold text-white">{remaining}</span> of {cap} founding spots left
                  </>
                ) : (
                  <>Reserve one of the first <span className="font-bold text-white">500</span> Founding Athlete spots</>
                )}
              </p>
            </div>
          </button>
          {iAmClaimed && mySlug && (
            <a
              href={`/l/${mySlug}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-300 text-black text-[10px] font-bold uppercase tracking-widest ring-1 ring-amber-200/50 hover:bg-amber-200 flex-shrink-0"
              data-testid="founding-banner-my-profile"
            >
              My link <ArrowUpRight className="w-3 h-3" />
            </a>
          )}
          <button
            type="button"
            onClick={toggle}
            className="w-8 h-8 rounded-full bg-black/30 hover:bg-black/40 flex items-center justify-center flex-shrink-0 transition"
            aria-expanded={open}
            aria-label={open ? 'Hide leaderboard' : 'Show leaderboard'}
            data-testid="founding-banner-toggle"
          >
            <ChevronDown className={`w-4 h-4 text-amber-200 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Leaderboard drawer */}
        {open && (
          <div className="relative border-t border-amber-200/20 bg-black/30 backdrop-blur-sm" data-testid="founding-banner-leaderboard">
            <div className="px-4 py-3 flex items-center justify-between">
              <p className="font-display text-[10px] uppercase tracking-[0.32em] text-amber-200/85 font-bold">
                The First 500 · Live scoreboard
              </p>
              <p className="text-[10px] uppercase tracking-widest text-amber-200/60 tabular-nums">
                {claimed ?? 0} / {cap}
              </p>
            </div>
            {boardLoading ? (
              <div className="px-4 pb-4 text-center text-white/50 text-xs">
                <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> loading scoreboard…
              </div>
            ) : board.length === 0 ? (
              <div className="px-4 pb-4 text-center text-white/60 text-xs leading-snug">
                Nobody&apos;s claimed a spot yet. Be #1 — tap <span className="font-bold text-amber-200">Join as an athlete now</span>.
              </div>
            ) : (
              <ul className="max-h-[280px] overflow-y-auto divide-y divide-white/5" style={{ overscrollBehavior: 'contain' }}>
                {board.map((row) => {
                  // iter505 · Route to the athlete's Network profile
                  // by default; only fall back to their FLYT ID™ (`/l/`)
                  // when they haven't set up any network data yet
                  // (no sport, no team, not verified — i.e. bare stub).
                  const hasNetworkProfile = !!(row.sport || row.team || row.verified || row.photo);
                  const targetHref = hasNetworkProfile ? `/network/${row.slug}` : `/l/${row.slug}`;
                  return (
                  <li key={row.slug}>
                    <a
                      href={targetHref}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition"
                      data-testid={`founding-board-${row.slug}`}
                    >
                      <span className="w-8 text-center font-display text-[11px] font-bold text-amber-200 tabular-nums flex-shrink-0">
                        #{row.founding_athlete_number}
                      </span>
                      <div className="w-7 h-7 rounded-full bg-white/10 overflow-hidden flex-shrink-0">
                        {row.photo && (
                          <img
                            src={row.photo.startsWith('http') ? row.photo : `${BACKEND_URL}${row.photo}`}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white font-semibold truncate flex items-center gap-1">
                          {row.name}
                          {row.verified && <ShieldCheck className="w-3 h-3 text-blue-400 flex-shrink-0" />}
                        </p>
                        {(row.sport || row.team) && (
                          <p className="text-[10px] uppercase tracking-widest text-white/40 truncate">
                            {[row.sport, row.team].filter(Boolean).join(' · ')}
                          </p>
                        )}
                      </div>
                      <ArrowUpRight className="w-3.5 h-3.5 text-white/40 flex-shrink-0" />
                    </a>
                  </li>
                );})}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// MODAL
// ─────────────────────────────────────────────────────────────────────

const PERKS = [
  { title: 'Founding Athlete badge', body: 'A verified amber-glow badge on your public FLYT ID™ — signals you were here first.' },
  { title: 'Direct line to the founder', body: 'A dedicated tab in the founder inbox so your notes never get lost in the shuffle.' },
  { title: 'First look at every drop', body: 'Roster tools, Studios features, and NIL matches ship to Founding Athletes before anyone else.' },
  { title: 'Free for life', body: 'Founding Athletes never pay for Flythouse. Your public FLYT ID™, Network profile, analytics, and every future Athlete Premium feature stay free forever — no matter how Flythouse tiers evolve.' },
];

export function FoundingAthleteModal({ open, onClose, defaultTab = 'overview' }) {
  const { user } = useUserAuth();
  const { stats, refresh } = useFoundingAthleteStats(open);
  const [tab, setTab] = useState(defaultTab); // "overview" | "contact"
  const [contactBusy, setContactBusy] = useState(false);
  const [contactMsg, setContactMsg] = useState('');
  const [claimBusy, setClaimBusy] = useState(false);
  const [justClaimed, setJustClaimed] = useState(null); // { number } after successful claim
  // iter291 v50 · Claim-form state. Athletes fill this out BEFORE we
  // lock in their founding spot so we filter out low-effort fakes.
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [form, setForm] = useState({ full_name: '', school: '', sport: '', grade_year: '', grade_year_custom: '', jersey_number: '' });
  const GRADE_OPTIONS = ['Freshman', 'Sophomore', 'Junior', 'Senior', 'Grad', 'Pro (specify)'];

  useEffect(() => {
    if (open) {
      setJustClaimed(null);
      // Only honor `defaultTab='contact'` for athletes who can actually
      // message the founder — everyone else lands on the Program tab.
      const canContact = (user?.persona || '').toLowerCase() === 'athlete';
      setTab(defaultTab === 'contact' && canContact ? 'contact' : 'overview');
    }
  }, [open, defaultTab, user?.persona]);

  // iter291 · Lock the page scroll while the modal is up. Without this,
  // the modal renders behind the sticky nav on iOS and the athlete has
  // to scroll down to find it — the exact bug Jay reported.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const isAthlete = (user?.persona || '').toLowerCase() === 'athlete';
  const remaining = stats?.remaining ?? null;
  const closed = !!stats?.closed;

  const handleClaim = useCallback(async () => {
    if (!user) {
      toast.error('Sign up as an athlete first, then claim your spot.');
      onClose?.();
      window.location.href = '/signup?persona=athlete';
      return;
    }
    // iter291 v50 · Enforce the claim form before hitting the API.
    // Half-filled forms don't claim — spot stays open per Jay's spec.
    const gradePicked = form.grade_year;
    const gradeFinal  = gradePicked === 'Pro (specify)' ? (form.grade_year_custom || '').trim() : gradePicked;
    if (!form.full_name.trim() || !form.school.trim() || !form.sport.trim() || !gradeFinal) {
      toast.error('Fill out every required field to lock in your spot.');
      setShowClaimForm(true);
      return;
    }
    setClaimBusy(true);
    try {
      const { data } = await axios.post(`${API}/founding-athletes/claim`, {
        full_name:     form.full_name.trim(),
        school:        form.school.trim(),
        sport:         form.sport.trim(),
        grade_year:    gradeFinal,
        jersey_number: (form.jersey_number || '').trim(),
      }, { withCredentials: true });
      setJustClaimed({ number: data.founding_athlete_number, slug: data.slug });
      // iter527 · Softened from "You're locked in as #N!" to a review-
      // pending message. Every founding-athlete submission is now
      // treated as an APPLICATION that Jay personally reviews before
      // the badge is confirmed — this sets the right expectation up
      // front so athletes don't think they're done the moment they
      // submit, and gives Jay a safeguard against spam or off-brand
      // applicants (see the admin approval queue in a follow-up).
      toast.success(data.already_claimed
        ? `Your Founding Athlete application (#${data.founding_athlete_number}) is on file — Jay will confirm shortly.`
        : `Application received — Jay will review and confirm your spot personally.`);
      refresh(true);
      try {
        sessionStorage.setItem('founding_athlete_just_claimed', String(data.founding_athlete_number));
        if (data.slug) sessionStorage.setItem('founding_athlete_just_claimed_slug', data.slug);
      } catch { /* private mode — non-fatal */ }
      setTimeout(() => {
        window.location.href = '/profile?founding_claim=1';
      }, 1400);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 404) {
        toast.error('Set up your athlete profile first, then come back to claim your badge.');
      } else {
        toast.error(detail || 'Could not claim spot — please try again.');
      }
    } finally {
      setClaimBusy(false);
    }
  }, [user, onClose, refresh, form]);

  const handleContact = useCallback(async (e) => {
    e?.preventDefault?.();
    const msg = contactMsg.trim();
    if (!msg) {
      toast.error('Add a quick note so I know what you need.');
      return;
    }
    setContactBusy(true);
    try {
      await axios.post(
        `${API}/founding-athletes/contact`,
        { message: msg, subject: 'Founding Athlete direct note' },
        { withCredentials: true },
      );
      toast.success("Note delivered — I'll get back to you personally.");
      setContactMsg('');
      setTab('overview');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send — please try again.');
    } finally {
      setContactBusy(false);
    }
  }, [contactMsg]);

  if (!open) return null;

  const canMessageFounder = isAthlete && (justClaimed?.number || stats?.founding_athlete_number);
  // Only surface the "Message Founder" tab to athletes who have
  // already claimed their spot — otherwise the tab reads as a perk
  // they haven't unlocked yet. Non-athletes see program info only.

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 modal-safe-pad-bottom"
      role="dialog"
      aria-modal="true"
      data-testid="founding-athlete-modal"
    >
      <div
        className="fixed inset-0 bg-black/75 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Glass case — amber ring + inner glow */}
      <div
        className="relative w-full max-w-md rounded-3xl border border-amber-200/20 overflow-hidden my-auto flex flex-col modal-card-mobile-max-h"
        style={{
          background: 'linear-gradient(180deg, rgba(24,18,4,0.94), rgba(9,9,11,0.98))',
          boxShadow: '0 40px 80px -20px rgba(0,0,0,0.9), 0 0 60px -18px rgba(250,204,21,0.45), inset 0 1px 0 rgba(255,255,255,0.06)',
        }}
      >
        {/* Ambient amber blob */}
        <span aria-hidden className="absolute -top-16 -left-16 w-52 h-52 rounded-full bg-amber-400/25 blur-3xl pointer-events-none" />
        <span aria-hidden className="absolute -bottom-16 -right-16 w-52 h-52 rounded-full bg-amber-500/20 blur-3xl pointer-events-none" />

        <button
          type="button"
          onClick={onClose}
          className="absolute top-3.5 right-3.5 z-10 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition"
          aria-label="Close"
          data-testid="founding-athlete-modal-close"
        >
          <X className="w-4 h-4 text-white/70" />
        </button>

        <div className="relative p-6 sm:p-7 overflow-y-auto">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-3.5 h-3.5 text-amber-300" />
            <p className="font-display text-[10px] uppercase tracking-[0.32em] text-amber-200/90 font-bold">
              Founding Athletes
            </p>
          </div>
          <h2 className="font-display text-2xl uppercase text-white tracking-tight leading-tight">
            The first 500 athletes on Flythouse
          </h2>

          {/* Live counter — the "glass case" spots-left readout */}
          <div className="mt-5 rounded-2xl p-4 bg-black/40 ring-1 ring-inset ring-amber-200/20 backdrop-blur">
            <div className="flex items-baseline justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.28em] text-amber-200/70 font-bold">Spots left</p>
                <p className="mt-1 font-display text-5xl text-amber-100 leading-none tabular-nums" data-testid="founding-athlete-remaining">
                  {remaining ?? '—'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-[0.28em] text-white/40 font-bold">Claimed</p>
                <p className="mt-1 text-lg text-white/80 tabular-nums">{stats?.claimed ?? '—'} <span className="text-white/40">/ {stats?.cap || 250}</span></p>
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: stats ? `${Math.min(100, (stats.claimed / (stats.cap || 250)) * 100)}%` : '0%',
                  background: 'linear-gradient(90deg, #fde68a, #f59e0b)',
                }}
              />
            </div>
          </div>

          {/* Segmented control — Program / Message Founder.
              The Message tab is a Founding Athlete perk, so it only
              appears for athletes who have already claimed their spot
              (or the just-claimed state). Everyone else sees the
              Program overview only — no phantom "Message" button. */}
          {canMessageFounder ? (
            <div className="mt-5 grid grid-cols-2 gap-1.5 p-1 rounded-full bg-white/5 ring-1 ring-inset ring-white/10">
              {[
                { id: 'overview', label: 'Program' },
                { id: 'contact',  label: 'Message Founder' },
              ].map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={`py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest transition ${
                    tab === t.id ? 'bg-amber-300 text-black' : 'text-white/70 hover:text-white'
                  }`}
                  data-testid={`founding-modal-tab-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          ) : null}

          {tab === 'overview' && (
            <div className="mt-4 space-y-2.5">
              {PERKS.map(p => (
                <div key={p.title} className="flex gap-3 items-start">
                  <div className="mt-0.5 w-5 h-5 rounded-full bg-amber-300/15 ring-1 ring-amber-200/40 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-amber-200" strokeWidth={2.5} />
                  </div>
                  <div>
                    <p className="text-sm text-white font-semibold leading-tight">{p.title}</p>
                    <p className="text-xs text-white/60 leading-snug mt-0.5">{p.body}</p>
                  </div>
                </div>
              ))}

              {/* CTA row */}
              <div className="pt-3">
                {justClaimed ? (
                  <div
                    className="rounded-2xl p-4 bg-amber-400/10 ring-1 ring-inset ring-amber-300/30 text-center"
                    data-testid="founding-application-pending"
                  >
                    <div className="inline-flex items-center gap-1.5 mb-1">
                      <ShieldCheck className="w-4 h-4 text-amber-200" />
                      <p className="font-display text-[10px] uppercase tracking-[0.28em] text-amber-200 font-bold">Application received</p>
                    </div>
                    <p className="text-white font-display text-2xl leading-tight">
                      Jay will review &amp; confirm you as a Founding Athlete
                    </p>
                    <p className="text-xs text-white/70 mt-2 leading-relaxed">
                      Your submission is in the review queue (position #{justClaimed.number}). Jay personally reviews every applicant to keep this founding class real. You&apos;ll get a confirmation once approved — check your Flythouse inbox and email.
                    </p>
                  </div>
                ) : closed ? (
                  <div className="rounded-2xl p-4 bg-black/40 ring-1 ring-inset ring-white/10 text-center text-white/70 text-sm">
                    All 500 founding spots are claimed. Follow along — we&apos;ll open a new tier next.
                  </div>
                ) : (
                  <>
                    {showClaimForm ? (
                      /* iter311 · Light-theme card so athletes see high-
                         contrast black text on white while filling this
                         out — Jay asked for this specifically. */
                      <div className="space-y-2.5 rounded-2xl bg-white p-4 ring-1 ring-black/10 shadow-lg" data-testid="founding-claim-form">
                        <div>
                          <label className="text-[10px] uppercase tracking-widest text-black/70 font-bold">Full name *</label>
                          <input
                            type="text"
                            value={form.full_name}
                            onChange={(e) => setForm(f => ({ ...f, full_name: e.target.value }))}
                            placeholder="First Last"
                            className="mt-1 w-full rounded-xl bg-white ring-1 ring-inset ring-black/10 focus:ring-amber-500/60 outline-none px-3 py-2 text-sm text-black placeholder:text-black/40"
                            data-testid="claim-full-name"
                            autoComplete="name"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] uppercase tracking-widest text-black/70 font-bold">School *</label>
                            <input
                              type="text"
                              value={form.school}
                              onChange={(e) => setForm(f => ({ ...f, school: e.target.value }))}
                              placeholder="e.g. Texas"
                              className="mt-1 w-full rounded-xl bg-white ring-1 ring-inset ring-black/10 focus:ring-amber-500/60 outline-none px-3 py-2 text-sm text-black placeholder:text-black/40"
                              data-testid="claim-school"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] uppercase tracking-widest text-black/70 font-bold">Sport *</label>
                            <input
                              type="text"
                              value={form.sport}
                              onChange={(e) => setForm(f => ({ ...f, sport: e.target.value }))}
                              placeholder="e.g. Football"
                              className="mt-1 w-full rounded-xl bg-white ring-1 ring-inset ring-black/10 focus:ring-amber-500/60 outline-none px-3 py-2 text-sm text-black placeholder:text-black/40"
                              data-testid="claim-sport"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] uppercase tracking-widest text-black/70 font-bold">Grade / league *</label>
                            <select
                              value={form.grade_year}
                              onChange={(e) => setForm(f => ({ ...f, grade_year: e.target.value }))}
                              className="mt-1 w-full rounded-xl bg-white ring-1 ring-inset ring-black/10 focus:ring-amber-500/60 outline-none px-3 py-2 text-sm text-black"
                              data-testid="claim-grade-year"
                            >
                              <option value="" className="bg-white text-black">Pick one</option>
                              {GRADE_OPTIONS.map(g => (
                                <option key={g} value={g} className="bg-white text-black">{g}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="text-[10px] uppercase tracking-widest text-black/70 font-bold">Jersey #</label>
                            <input
                              type="text"
                              value={form.jersey_number}
                              onChange={(e) => setForm(f => ({ ...f, jersey_number: e.target.value }))}
                              placeholder="Optional"
                              className="mt-1 w-full rounded-xl bg-white ring-1 ring-inset ring-black/10 focus:ring-amber-500/60 outline-none px-3 py-2 text-sm text-black placeholder:text-black/40"
                              data-testid="claim-jersey"
                              inputMode="numeric"
                              maxLength={4}
                            />
                          </div>
                        </div>
                        {form.grade_year === 'Pro (specify)' && (
                          <div>
                            <label className="text-[10px] uppercase tracking-widest text-black/70 font-bold">League + team *</label>
                            <input
                              type="text"
                              value={form.grade_year_custom}
                              onChange={(e) => setForm(f => ({ ...f, grade_year_custom: e.target.value }))}
                              placeholder="e.g. NFL · Dallas Cowboys"
                              className="mt-1 w-full rounded-xl bg-white ring-1 ring-inset ring-black/10 focus:ring-amber-500/60 outline-none px-3 py-2 text-sm text-black placeholder:text-black/40"
                              data-testid="claim-pro-league"
                            />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={handleClaim}
                          disabled={claimBusy}
                          className="mt-3 w-full inline-flex items-center justify-center gap-2 py-3 rounded-full font-bold text-black text-sm tracking-wide transition active:scale-95 disabled:opacity-70"
                          style={{
                            background: 'linear-gradient(90deg, #fde68a, #f59e0b)',
                            boxShadow: '0 12px 28px -10px rgba(245,158,11,0.6)',
                          }}
                          data-testid="founding-modal-submit-btn"
                        >
                          {claimBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                          {claimBusy ? 'Reserving your spot…' : 'Submit & claim my spot'}
                        </button>
                        <p className="text-[10px] uppercase tracking-widest text-white/40 text-center">
                          Half-filled forms don&apos;t reserve a spot.
                        </p>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowClaimForm(true)}
                        className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full font-bold text-black text-sm tracking-wide transition active:scale-95"
                        style={{
                          background: 'linear-gradient(90deg, #fde68a, #f59e0b)',
                          boxShadow: '0 12px 28px -10px rgba(245,158,11,0.6)',
                        }}
                        data-testid="founding-modal-join-btn"
                      >
                        <Sparkles className="w-4 h-4" />
                        Join as an athlete now
                      </button>
                    )}
                  </>
                )}
                {!showClaimForm && !justClaimed && !closed && (
                  <p className="text-[10px] uppercase tracking-widest text-white/40 text-center mt-2">
                    Fill a quick form to verify you&apos;re a real athlete.
                  </p>
                )}
              </div>
            </div>
          )}

          {tab === 'contact' && canMessageFounder && (
            <form onSubmit={handleContact} className="mt-4">
              <label className="block text-[10px] uppercase tracking-[0.28em] text-amber-200/75 font-bold mb-2">
                Direct note to the founder
              </label>
              <textarea
                value={contactMsg}
                onChange={(e) => setContactMsg(e.target.value)}
                rows={5}
                placeholder="Ideas, feedback, deals you want on Flythouse — anything. I read every one."
                className="w-full rounded-2xl bg-black/50 ring-1 ring-inset ring-white/10 focus:ring-amber-300/40 outline-none px-4 py-3 text-sm text-white placeholder:text-white/30 resize-none"
                data-testid="founding-contact-textarea"
              />
              <button
                type="submit"
                disabled={contactBusy}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 py-3 rounded-full font-bold text-black text-sm transition active:scale-95 disabled:opacity-70"
                style={{
                  background: 'linear-gradient(90deg, #fde68a, #f59e0b)',
                  boxShadow: '0 12px 28px -10px rgba(245,158,11,0.6)',
                }}
                data-testid="founding-contact-send"
              >
                {contactBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {contactBusy ? 'Sending…' : 'Send to founder'}
              </button>
              <p className="text-[10px] uppercase tracking-widest text-white/40 text-center mt-2">
                Lands in the Founder Inbox under Founding Athletes.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// STANDALONE CONTACT BUTTON — used on AthleteMePage above "Send to your agent"
// ─────────────────────────────────────────────────────────────────────

export function FoundingContactButton({ className = '' }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`group relative w-full overflow-hidden rounded-2xl px-4 py-3 text-left transition active:scale-[0.99] ${className}`}
        style={{ background: AMBER_GRADIENT, boxShadow: AMBER_GLOW_SHADOW }}
        data-testid="founding-athlete-contact-btn"
      >
        <span aria-hidden className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-amber-300/25" />
        <span aria-hidden className="absolute -top-12 -right-8 w-32 h-32 rounded-full bg-amber-300/25 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-black/40 ring-1 ring-amber-200/40 flex items-center justify-center flex-shrink-0">
            <MessageCircle className="w-4 h-4 text-amber-200" strokeWidth={2.2} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-display text-[10px] uppercase tracking-[0.28em] text-amber-200/85 leading-none">
              Founding Athlete perk
            </p>
            <p className="mt-1 text-sm text-white leading-snug">
              Message the founder directly
            </p>
          </div>
          <ArrowRight className="w-4 h-4 text-amber-200/80 group-hover:translate-x-0.5 transition flex-shrink-0" />
        </div>
      </button>
      <FoundingAthleteModal open={open} onClose={() => setOpen(false)} defaultTab="contact" />
    </>
  );
}

export default FoundingAthletePill;
